// ============================================================================
// USER SERVICE - Role-based user management
// ============================================================================
// File: src/services/userService.js
// ============================================================================

import { db, auth } from '../firebase-config';
import {
  collection, doc, setDoc, getDoc, updateDoc, deleteDoc,
  serverTimestamp, query, where, getDocs
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { storage } from '../firebase-config';

// ── Default permissions per role ────────────────────────────────────────────
const DEFAULT_PERMISSIONS = {
  admin: {
    canManageCases: true,
    canManageClients: true,
    canManageBilling: true,
    canManageUsers: true,
    canViewAnalytics: true,
    canDeleteRecords: true,
    canManageDocuments: true,
    canManageFirmSettings: true
  },
  lawyer: {
    canManageCases: true,
    canManageClients: true,
    canManageBilling: true,
    canManageUsers: false,
    canViewAnalytics: true,
    canDeleteRecords: false,
    canManageDocuments: true,
    canManageFirmSettings: false
  },
  paralegal: {
    canManageCases: true,
    canManageClients: true,
    canManageBilling: false,
    canManageUsers: false,
    canViewAnalytics: false,
    canDeleteRecords: false,
    canManageDocuments: true,
    canManageFirmSettings: false
  },
  secretary: {
    canManageCases: false,
    canManageClients: true,
    canManageBilling: false,
    canManageUsers: false,
    canViewAnalytics: false,
    canDeleteRecords: false,
    canManageDocuments: false,
    canManageFirmSettings: false
  }
};

// ── Create user profile ─────────────────────────────────────────────────────
// DEPRECATED (2026-09-24): no longer called from registration — see
// createFirm's matching deprecation note in firmService.js. New-firm
// bootstrap (firm + user profile + founder teamMember, atomically) goes
// through bootstrapNewFirm instead.
export const createUserProfile = async (userId, userData) => {
  try {
    const role = userData.role || 'lawyer';
    const userDoc = {
      id: userId,
      email: userData.email || '',
      displayName: userData.displayName || '',
      role,
      firmId: userData.firmId || '',
      permissions: DEFAULT_PERMISSIONS[role] || DEFAULT_PERMISSIONS.lawyer,
      createdAt: serverTimestamp(),
      invitedBy: userData.invitedBy || null,
      status: userData.status || 'active',
      phone: userData.phone || '',
      title: userData.title || '',
      photoURL: userData.photoURL || ''
    };
    await setDoc(doc(db, 'users', userId), userDoc);
    return { success: true, user: userDoc };
  } catch (error) {
    console.error('Error creating user profile:', error);
    return { success: false, error: error.message };
  }
};

// ── Get user profile ────────────────────────────────────────────────────────
// Registration reliability remediation (2026-09-24): "the document doesn't
// exist" and "the read was denied" are materially different conditions —
// callers (notably Login.js's idempotency/recovery checks) must not treat a
// permission-denied read as "no profile yet" and proceed as if this were a
// genuinely brand-new user. errorCode distinguishes them without exposing
// any sensitive detail beyond the Firebase SDK's own error code.
export const getUserProfile = async (userId) => {
  try {
    const userSnap = await getDoc(doc(db, 'users', userId));
    if (userSnap.exists()) {
      return { success: true, user: { id: userSnap.id, ...userSnap.data() } };
    }
    return { success: false, error: 'User profile not found', errorCode: 'not-found' };
  } catch (error) {
    console.error('[getUserProfile] Firestore read failed:', error.code, '(uid redacted)');
    return { success: false, error: error.message, errorCode: error.code || 'unknown' };
  }
};

// ── Get all users in a firm ─────────────────────────────────────────────────
export const getFirmUsers = async (firmId) => {
  try {
    const q = query(collection(db, 'users'), where('firmId', '==', firmId));
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    return { success: true, users };
  } catch (error) {
    console.error('Error getting firm users:', error);
    return { success: false, error: error.message };
  }
};

// ── Update user profile ─────────────────────────────────────────────────────
export const updateUserProfile = async (userId, updates) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user profile:', error);
    return { success: false, error: error.message };
  }
};

// ── Update user role (admin only) ───────────────────────────────────────────
export const updateUserRole = async (userId, role) => {
  try {
    const permissions = DEFAULT_PERMISSIONS[role] || DEFAULT_PERMISSIONS.lawyer;
    await updateDoc(doc(db, 'users', userId), {
      role,
      permissions,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user role:', error);
    return { success: false, error: error.message };
  }
};

// ── Remove a registered member from the firm (trusted server path) ──────────
// The ONLY supported removal: the removeFirmMember callable clears the
// member's firm membership (status, firmId, role, permissions), retires their
// roster seats and writes the audit event in ONE transaction — or changes
// nothing. There is deliberately no client-side fallback write: a failure
// (including a network error) is reported as a failure, never as success.
// expectedFirmId is the admin's current firm — the server refuses (reason
// MEMBERSHIP_CHANGED) if the member has since moved to another firm.
const REMOVE_MEMBER_FALLBACK_MESSAGES = {
  'functions/unavailable': 'The removal could not be completed — please try again.',
  'functions/deadline-exceeded': 'The removal could not be confirmed — please refresh and check before retrying.',
  'functions/internal': 'The removal failed — nothing was changed.',
};
export const removeFirmMember = async (targetUid, expectedFirmId) => {
  try {
    const fn = httpsCallable(getFunctions(), 'removeFirmMember');
    const { data } = await fn({ targetUid, expectedFirmId });
    return { success: true, alreadyRemoved: !!(data && data.alreadyRemoved) };
  } catch (error) {
    const reason = (error && error.details && error.details.reason) || null;
    const message = reason ? error.message : (REMOVE_MEMBER_FALLBACK_MESSAGES[error && error.code] || 'The removal could not be completed — please check your connection and try again.');
    return { success: false, reason, code: (error && error.code) || null, error: message };
  }
};

// ── Suspend / reactivate user ───────────────────────────────────────────────
export const updateUserStatus = async (userId, status) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      status,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user status:', error);
    return { success: false, error: error.message };
  }
};

// ── Invite user to firm ─────────────────────────────────────────────────────
// Seat limits (2026-10-15): invitations are created server-side (createFirmInvite),
// which enforces the plan's user limit and also creates the invitee's Team Hub
// placeholder — one person, one seat. `invitedBy` is the authenticated caller.
export const inviteUser = async (firmId, email, role, invitedBy, extra = {}) => {
  try {
    const r = await httpsCallable(getFunctions(), 'createFirmInvite')({ firmId, email: email.toLowerCase().trim(), role, name: extra.name || '', phone: extra.phone || '' });
    return { success: true, inviteId: r.data.inviteId, alreadyPending: !!r.data.alreadyPending };
  } catch (e) {
    console.error('Error inviting user:', e && e.code);
    return { success: false, error: (e && e.details && e.details.reason === 'SEAT_LIMIT_REACHED') ? "Your plan's user limit has been reached. Upgrade the plan, or remove a member or a pending invitation first." : (e && e.message) || 'Could not complete this request.', seatLimit: !!(e && e.details && e.details.reason === 'SEAT_LIMIT_REACHED') };
  }
};

// ── Check pending invite for email ──────────────────────────────────────────
// Registration reliability remediation (2026-09-24): same errorCode
// distinction as getUserProfile above — a permission-denied list here must
// not be treated as "genuinely no invite" by callers.
export const checkPendingInvite = async (email) => {
  try {
    const q = query(
      collection(db, 'invites'),
      where('email', '==', email.toLowerCase().trim()),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const invite = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
      return { success: true, invite };
    }
    return { success: false, error: 'No pending invite found', errorCode: 'not-found' };
  } catch (error) {
    console.error('[checkPendingInvite] Firestore query failed:', error.code, '(email redacted)');
    return { success: false, error: error.message, errorCode: error.code || 'unknown' };
  }
};

// ── Accept invite ───────────────────────────────────────────────────────────
// C1 invite-takeover remediation (2026-10-07): there is no client-side invite
// write any more. Acceptance (and the invite's accepted transition) happens
// only in the acceptFirmInvite callable — see acceptInvitation below.

// ── Get pending invites for a firm ──────────────────────────────────────────
export const getFirmInvites = async (firmId) => {
  try {
    const q = query(
      collection(db, 'invites'),
      where('firmId', '==', firmId),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(q);
    const invites = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    return { success: true, invites };
  } catch (error) {
    console.error('Error getting firm invites:', error);
    return { success: false, error: error.message };
  }
};

// ── Delete invite ───────────────────────────────────────────────────────────
export const deleteInvite = async (inviteId) => {
  try {
    await deleteDoc(doc(db, 'invites', inviteId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting invite:', error);
    return { success: false, error: error.message };
  }
};

// ── Permission check helper ─────────────────────────────────────────────────
export const hasPermission = (userProfile, permission) => {
  return userProfile?.permissions?.[permission] === true;
};

// ── Get default permissions for a role ───────────────────────────────────────
export const getDefaultPermissions = (role) => {
  return DEFAULT_PERMISSIONS[role] || DEFAULT_PERMISSIONS.lawyer;
};

// ── Upload profile picture ──────────────────────────────────────────────────
export const updateProfilePicture = async (userId, file) => {
  try {
    const storageRef = ref(storage, `profilePictures/${userId}/${file.name}`);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);

    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { photoURL: downloadURL });

    return { success: true, photoURL: downloadURL };
  } catch (error) {
    console.error('Error updating profile picture:', error);
    return { success: false, error: error.message };
  }
};
// ── Pending invitations: explicit choice only (firm-move lifecycle) ─────────
// No invitation is ever accepted automatically. The user sees their own
// pending invitations (firm name + intended role, served by the
// listMyPendingInvites callable) and explicitly accepts ONE. Accepting while
// in a firm requires expectedCurrentFirmId = that firm (the server re-checks it
// against the authoritative profile and refuses a silent move); a firmless or
// removed user passes null.
// M1 (2026-10-08): for an account whose email Firebase Auth has NOT verified,
// the server returns no invitation details — only pendingCount and
// emailVerificationRequired — so the app asks the person to verify first.
// pendingCount falls back to invites.length for an older server.
export const listMyPendingInvites = async () => {
  try {
    const fn = httpsCallable(getFunctions(), 'listMyPendingInvites');
    let { data } = await fn({});
    // Just verified, but the ID token predates it: refresh once and ask again.
    if (data && data.emailVerificationRequired && auth.currentUser && auth.currentUser.emailVerified) {
      await auth.currentUser.getIdToken(true);
      ({ data } = await fn({}));
    }
    const invites = (data && data.invites) || [];
    const pendingCount = data && typeof data.pendingCount === 'number' ? data.pendingCount : invites.length;
    return { success: true, invites, pendingCount, emailVerificationRequired: !!(data && data.emailVerificationRequired) };
  } catch (error) {
    return { success: false, invites: [], error: error.message, code: error.code || null };
  }
};

export const acceptInvitation = async (inviteId, expectedCurrentFirmId, displayName) => {
  try {
    const fn = httpsCallable(getFunctions(), 'acceptFirmInvite');
    const payload = { inviteId, expectedCurrentFirmId: expectedCurrentFirmId || null };
    if (displayName) payload.displayName = displayName;
    const { data } = await fn(payload);
    return { success: true, ...data };
  } catch (error) {
    const reason = (error && error.details && error.details.reason) || null;
    return {
      success: false, reason, code: (error && error.code) || null,
      error: reason ? error.message : 'The invitation could not be accepted — please check your connection and try again. Nothing was changed.',
    };
  }
};
